<template>
  <div class="drawer-root">
    <Transition name="drawer-backdrop">
      <div
        v-if="open && backdrop"
        drawer-backdrop
        class="fixed inset-0 z-50 bg-gray-900/50 dark:bg-gray-900/80"
        @click="closeDrawer"
      ></div>
    </Transition>
    <Transition name="drawer-slide" @after-leave="onClosed">
      <div
        v-if="open"
        role="dialog"
        aria-modal="true"
        class="fixed top-0 right-0 z-60 h-100dvh w-full md:max-h-97dvh rounded-lg max-md:rounded-none md:mr-4 md:mt-4 md:w-[540px] overflow-x-hidden bg-white dark:bg-gray-800 drawer grid"
      >
        <div
          v-if="includeHeader"
          class="absolute bg-white text-white w-full z-10 text-xs"
        >
          <slot name="drawerHeader"> </slot>
        </div>
        <div
          :class="[
            'p-4 relative rounded-t-lg bg-white z-20',
            { 'mt-9 header-box-shadow': includeHeader }
          ]"
        >
          <slot name="title">
            <div class="flex gap-3">
              <div v-if="backArrow" class="cursor-pointer" @click="closeDrawer">
                <CaretLeft class="h-6" />
              </div>
              <h5
                class="mb-4 inline-flex items-center text-base font-semibold text-gray-900"
              >
                {{ title }}
              </h5>
            </div>
          </slot>
          <button
            v-if="!backArrow"
            type="button"
            class="absolute right-2.5 top-2.5 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white cursor-pointer"
            @click="closeDrawer"
          >
            <CloseIcon class="w-4" />
            <span class="sr-only">Close menu</span>
          </button>
        </div>
        <div class="overflow-y-auto">
          <slot></slot>
        </div>
        <div
          v-if="!noFooter"
          class="sticky bottom-0 p-4 bg-white flex justify-center w-full"
        >
          <slot name="footer"></slot>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

import CaretLeft from "@/images/CaretLeft.svg?component";
import CloseIcon from "@/images/x.svg?component";

const emit = defineEmits(["hide-drawer", "show-drawer"]);
withDefaults(
  defineProps<{
    title?: string;
    includeHeader?: boolean;
    backArrow?: boolean;
    noFooter?: boolean;
    backdrop?: boolean;
  }>(),
  {
    title: "",
    backdrop: true,
    backArrow: false,
    includeHeader: false,
    noFooter: false
  }
);

// Open state lives entirely in Vue reactivity: the panel and its backdrop are
// v-if children of <Transition>, so Vue owns every mount/unmount and no
// imperative library holds the node. Closing sets `open` false, the leave
// transition plays, and @after-leave emits hide-drawer for the parent to
// unmount — the node is always removed by the same system that created it.
const open = ref(false);

const closeDrawer = () => {
  open.value = false;
};

const onClosed = () => emit("hide-drawer");

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape") closeDrawer();
};

onMounted(() => {
  document.addEventListener("keydown", onKeydown);
  // Mount closed, then flip open so the slide-in transition plays from the
  // off-screen position instead of popping in already open.
  open.value = true;
  emit("show-drawer");
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown);
});
</script>

<style lang="scss">
.drawer.grid {
  grid-template-rows: 1fr 14fr 1fr;
}
.header-box-shadow {
  box-shadow: 0px -5px 11px -3px rgba(0, 0, 0, 0.15);
}
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.3s ease-in-out;
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
}
.drawer-backdrop-enter-active,
.drawer-backdrop-leave-active {
  transition: opacity 0.3s ease;
}
.drawer-backdrop-enter-from,
.drawer-backdrop-leave-to {
  opacity: 0;
}
</style>
